import togeojson from '@mapbox/togeojson';
import JSZip from 'jszip';

export interface GeoJSONFeature {
  type: 'Feature';
  geometry: { type: string; coordinates?: any; geometries?: any[] };
  properties?: Record<string, unknown>;
}

export interface GeoJSONCollection {
  type: 'FeatureCollection';
  features: GeoJSONFeature[];
}

const EMPTY: GeoJSONCollection = { type: 'FeatureCollection', features: [] };

const kmlToGeoJSON = (kmlText: string): GeoJSONCollection =>
  togeojson.kml(new DOMParser().parseFromString(kmlText, 'text/xml'));

async function fetchGeoJSON(url: string): Promise<GeoJSONCollection> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const path = url.toLowerCase();
  if (path.endsWith('.kmz')) {
    const zip = await JSZip.loadAsync(await response.arrayBuffer());
    const kmlFile = Object.keys(zip.files).find(name => name.toLowerCase().endsWith('.kml'));
    if (!kmlFile) {
      throw new Error('KMZ without a KML file');
    }
    return kmlToGeoJSON(await zip.files[kmlFile].async('text'));
  }
  if (path.endsWith('.kml')) {
    return kmlToGeoJSON(await response.text());
  }
  if (path.endsWith('.geojson')) {
    return JSON.parse(await response.text());
  }
  throw new Error('Unsupported file type');
}

/** Load a KML, KMZ or GeoJSON file as GeoJSON; a file that can't be read yields no features. */
export async function loadGeoJSON(url: string): Promise<GeoJSONCollection> {
  try {
    const geojson = await fetchGeoJSON(url);
    return { ...geojson, features: (geojson.features || []).filter(feature => feature.geometry) };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Could not load map geometry from ${url}:`, error);
    return EMPTY;
  }
}
